import React, { useEffect, useState } from 'react';
import { Star, Send, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, size = 'md', onChange }) => {
  const sizes = { sm: 'h-3.5 w-3.5', md: 'h-5 w-5', lg: 'h-7 w-7' };
  const cls = sizes[size] || sizes.md;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= Math.round(value);
        const star = (
          <Star
            key={i}
            className={cls}
            style={{
              color: filled ? '#D4A373' : '#5A4A3F',
              fill: filled ? '#D4A373' : 'transparent',
              cursor: onChange ? 'pointer' : 'default',
            }}
            strokeWidth={2}
          />
        );
        if (onChange) {
          return (
            <button
              key={i}
              type="button"
              onClick={() => onChange(i)}
              data-testid={`star-input-${i}`}
              className="hover:scale-110 transition-transform"
              aria-label={`${i} star${i > 1 ? 's' : ''}`}
            >
              {star}
            </button>
          );
        }
        return star;
      })}
    </div>
  );
};

const ReviewSection = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/${productId}`);
      setReviews(data || []);
    } catch (e) {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const avg =
    reviews.length === 0
      ? 0
      : reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !comment.trim()) return;
    setSubmitting(true);
    setSubmitMsg('');
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        name: name.trim(),
        rating,
        comment: comment.trim(),
      });
      setSubmitMsg('Thanks — your review has been posted.');
      setName('');
      setComment('');
      setRating(5);
      setShowForm(false);
      await fetchReviews();
    } catch (err) {
      setSubmitMsg('Could not post review. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="reviews-section" className="mt-8">
      <div
        className="rounded-lg p-6 sm:p-8"
        style={{
          background: 'linear-gradient(135deg, #1A1513 0%, #221A14 100%)',
          border: '1px solid #362B25',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h3 className="text-2xl font-bold mb-2" style={{ color: '#F7F3EE', fontFamily: 'Playfair Display, serif' }}>
              Research Reviews
            </h3>
            {reviews.length > 0 ? (
              <div className="flex items-center gap-3">
                <Stars value={avg} size="md" />
                <span className="text-sm font-bold" style={{ color: '#D4A373' }} data-testid="review-avg">
                  {avg.toFixed(1)}
                </span>
                <span className="text-sm" style={{ color: '#B5A79E' }} data-testid="review-count">
                  · {reviews.length} review{reviews.length === 1 ? '' : 's'}
                </span>
              </div>
            ) : (
              <p className="text-sm" style={{ color: '#B5A79E' }}>
                No reviews yet — be the first to share your experience.
              </p>
            )}
          </div>
          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              data-testid="open-review-form"
              className="px-5 py-2.5 rounded-md font-bold text-sm uppercase tracking-wide transition-all hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, #D4A373 0%, #B5895F 100%)',
                color: '#0C0A09',
              }}
            >
              Write a Review
            </button>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            data-testid="review-form"
            className="rounded-md p-5 mb-6 space-y-4"
            style={{ backgroundColor: '#0C0A09', border: '1px dashed rgba(212, 163, 115, 0.4)' }}
          >
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2" style={{ color: '#B5A79E' }}>
                Your Rating
              </label>
              <Stars value={rating} size="lg" onChange={setRating} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2" style={{ color: '#B5A79E' }}>
                Your Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Alex M."
                maxLength={60}
                required
                data-testid="review-name-input"
                className="w-full px-4 py-3 rounded-md focus:outline-none"
                style={{
                  backgroundColor: '#1A1513',
                  border: '1px solid #362B25',
                  color: '#F7F3EE',
                }}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2" style={{ color: '#B5A79E' }}>
                Your Review
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your research observations…"
                maxLength={800}
                required
                rows={4}
                data-testid="review-comment-input"
                className="w-full px-4 py-3 rounded-md focus:outline-none resize-none"
                style={{
                  backgroundColor: '#1A1513',
                  border: '1px solid #362B25',
                  color: '#F7F3EE',
                }}
              />
              <p className="text-xs mt-1 text-right" style={{ color: '#5A4A3F' }}>
                {comment.length}/800
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-review-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-md font-bold text-sm uppercase tracking-wide transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #D4A373 0%, #B5895F 100%)',
                  color: '#0C0A09',
                }}
              >
                <Send className="h-4 w-4" />
                {submitting ? 'Posting…' : 'Post Review'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setSubmitMsg(''); }}
                className="px-5 py-2.5 rounded-md font-bold text-sm uppercase tracking-wide"
                style={{ backgroundColor: 'transparent', color: '#B5A79E', border: '1px solid #362B25' }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {submitMsg && (
          <p className="text-sm mb-4" style={{ color: submitMsg.startsWith('Thanks') ? '#86EFAC' : '#FCA5A5' }}>
            {submitMsg}
          </p>
        )}

        {/* Reviews List */}
        {loading ? (
          <p className="text-sm" style={{ color: '#B5A79E' }}>Loading reviews…</p>
        ) : reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map((r) => (
              <div
                key={r.review_id}
                data-testid="review-item"
                className="rounded-md p-4"
                style={{ backgroundColor: '#0C0A09', border: '1px solid #362B25' }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(212, 163, 115, 0.15)' }}
                  >
                    <User className="h-5 w-5" style={{ color: '#D4A373' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                      <p className="font-bold text-sm" style={{ color: '#F7F3EE' }}>{r.name}</p>
                      <span className="text-xs" style={{ color: '#5A4A3F' }}>
                        {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <Stars value={r.rating} size="sm" />
                    <p className="text-sm mt-2 leading-relaxed" style={{ color: '#B5A79E' }}>
                      {r.comment}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ReviewSection;
export { Stars };
